import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { CircleSlash, CloudOff } from 'lucide-react';
import { createContext, useCallback, useContext, useEffect, useRef, useState, } from 'react';
import { CLAUDE_MARK_PATH, OPENAI_MARK_PATH } from './agentMarks.js';
import { StatusIcon } from './StatusIcon.js';
/**
 * Controlled-or-uncontrolled state: when `value` is defined the parent owns it (and `onChange`
 * reports edits); otherwise the component keeps its own state seeded from `initial`.
 */
export function useControllable(value, initial, onChange) {
    const [inner, setInner] = useState(initial);
    const controlled = value !== undefined;
    const current = controlled ? value : inner;
    const ref = useRef(current);
    ref.current = current;
    const set = useCallback((next) => {
        const v = typeof next === 'function' ? next(ref.current) : next;
        if (!controlled)
            setInner(v);
        onChange?.(v);
    }, [controlled, onChange]);
    return [current, set];
}
export function cx(...parts) {
    return parts.filter(Boolean).join(' ');
}
export const ViewerContext = createContext({ connection: 'live' });
export const useViewer = () => useContext(ViewerContext);
/** Ticking clock for live durations; frozen when the viewer context provides `now`. */
export function useNow(active, interval = 1000) {
    const { now: frozen } = useViewer();
    const [now, setNow] = useState(() => frozen ?? Date.now());
    useEffect(() => {
        if (frozen != null || !active)
            return;
        const id = setInterval(() => setNow(Date.now()), interval);
        return () => clearInterval(id);
    }, [active, frozen, interval]);
    return frozen ?? now;
}
// ---------------------------------------------------------------------------------------------
// Status: glyph (shape) + label + colour — never colour alone
// ---------------------------------------------------------------------------------------------
export const STATE_LABEL = {
    pending: 'Queued',
    streaming: 'Running',
    ok: 'Done',
    error: 'Failed',
    cancelled: 'Cancelled',
    'awaiting-user': 'Needs you',
    stale: 'Offline',
};
const STATE_TONE = {
    pending: 'idle',
    streaming: 'run',
    ok: 'ok',
    error: 'fail',
    cancelled: 'idle',
    'awaiting-user': 'wait',
    stale: 'idle',
};
export function toneOf(state) {
    return STATE_TONE[state];
}
/** ItemState → Lucide status icon kind (cancelled / stale have their own Lucide icons). */
const STATE_KIND = {
    pending: 'idle',
    streaming: 'run',
    ok: 'ok',
    error: 'fail',
    'awaiting-user': 'wait',
};
export function StatusGlyph({ state, size = 16, label, }) {
    const a11y = label
        ? { role: 'img', 'aria-label': label }
        : { 'aria-hidden': true };
    const cls = cx('vk-glyph', `vk-tone-${STATE_TONE[state]}`);
    if (state === 'stale') {
        return (_jsx("span", { className: cls, ...a11y, children: _jsx(CloudOff, { size: size, strokeWidth: 2 }) }));
    }
    if (state === 'cancelled') {
        return (_jsx("span", { className: cls, ...a11y, children: _jsx(CircleSlash, { size: size, strokeWidth: 2 }) }));
    }
    const kind = STATE_KIND[state] ?? 'idle';
    return (_jsx("span", { className: cls, ...a11y, children: _jsx(StatusIcon, { kind: kind, size: size }) }));
}
export function StatusPill({ state, label, size = 'md', className, }) {
    return (_jsxs("span", { className: cx('vk-pill', `vk-pill--${size}`, `vk-tone-${STATE_TONE[state]}`, className), children: [_jsx(StatusGlyph, { state: state, size: size === 'sm' ? 14 : 16 }), _jsx("span", { className: "vk-pill__label", children: label ?? STATE_LABEL[state] })] }));
}
// ---------------------------------------------------------------------------------------------
// Agent identity: official product mark + thin rule only (Claude warm / Codex teal)
// ---------------------------------------------------------------------------------------------
export const AGENT_NAME = { claude: 'Claude', codex: 'Codex' };
export function AgentGlyph({ agent, size = 16, withName = false, }) {
    return (_jsxs("span", { className: cx('vk-agent', `vk-agent--${agent}`), children: [_jsx("svg", { width: size, height: size, viewBox: "0 0 24 24", "aria-hidden": "true", className: "vk-agent__glyph", children: _jsx("path", { fill: "currentColor", d: agent === 'claude' ? CLAUDE_MARK_PATH : OPENAI_MARK_PATH }) }), withName ? (_jsx("span", { className: "vk-agent__name", children: AGENT_NAME[agent] })) : (_jsx("span", { className: "vk-sr", children: AGENT_NAME[agent] }))] }));
}
// ---------------------------------------------------------------------------------------------
// Context ring
// ---------------------------------------------------------------------------------------------
export function ContextRing({ pct, tokens, window: win, size = 22, showLabel = true, state, }) {
    const p = Math.max(0, Math.min(100, pct ?? 0));
    const tone = state === 'stale' ? 'idle' : p >= 90 ? 'fail' : p >= 75 ? 'wait' : 'accent';
    const r = 7;
    const c = 2 * Math.PI * r;
    const label = pct == null
        ? 'Context unknown'
        : `Context ${Math.round(p)}% used${tokens ? `, ${tokens}${win ? ` of ${win}` : ''} tokens` : ''}`;
    return (_jsxs("span", { className: cx('vk-ring', `vk-ring--${tone}`), title: label, children: [_jsxs("svg", { width: size, height: size, viewBox: "0 0 18 18", role: "img", "aria-label": label, children: [_jsx("circle", { cx: "9", cy: "9", r: r, fill: "none", stroke: "var(--vk-border-strong)", strokeWidth: "2.2" }), _jsx("circle", { cx: "9", cy: "9", r: r, fill: "none", stroke: "currentColor", strokeWidth: "2.2", strokeDasharray: `${(p / 100) * c} ${c}`, strokeLinecap: "round", transform: "rotate(-90 9 9)" })] }), showLabel ? (_jsx("span", { className: "vk-ring__label", "aria-hidden": "true", children: pct == null ? '—' : `${Math.round(p)}%` })) : null] }));
}
// ---------------------------------------------------------------------------------------------
// Buttons & chips
// ---------------------------------------------------------------------------------------------
export function Button({ variant = 'secondary', size = 'md', icon, children, className, ...rest }) {
    return (_jsxs("button", { type: "button", className: cx('vk-btn', `vk-btn--${variant}`, `vk-btn--${size}`, !children && 'vk-btn--icon', className), ...rest, children: [icon, children ? _jsx("span", { children: children }) : null] }));
}
export function Chip({ children, icon, tone, mono, className, title, }) {
    return (_jsxs("span", { className: cx('vk-chip', tone && `vk-tone-${tone}`, mono && 'vk-mono', className), title: title, children: [icon, _jsx("span", { className: "vk-chip__text", children: children })] }));
}
export function DiffStat({ additions, deletions }) {
    if (!additions && !deletions)
        return null;
    return (_jsxs("span", { className: "vk-diffstat", role: "img", "aria-label": `${additions} lines added, ${deletions} removed`, children: [_jsxs("span", { className: "vk-diffstat__add", children: ["+", additions] }), _jsxs("span", { className: "vk-diffstat__del", children: ["\u2212", deletions] })] }));
}
export function VisuallyHidden({ children }) {
    return _jsx("span", { className: "vk-sr", children: children });
}
//# sourceMappingURL=primitives.js.map